import type { IInvoiceStatusOptions } from '../Invoices/Invoice';
import type { IOrderInvoiceDetailList, IOrderList } from './Order';

export class Order implements IOrder {
  allInvoicesTransmitted: boolean = false;
  amountInvoices: NonNullable<number> = 0;
  companyDocument: NonNullable<ICompanyDocument> = CompanyDocument('');
  finalCustomerName: NonNullable<string> = '';
  invoiceDetails: NonNullable<IOrderInvoiceDetailList> = [];
  itemsPercentageInvoiced: NonNullable<number> = 0;
  netCommission: NonNullable<string> = 'R$ 0,00';
  orderDate: NonNullable<string> = '';
  orderNumber: NonNullable<string> = '';
  orderStatus: NonNullable<IOrderStatusKeys> = 'PROCESSING';
  paymentMethod: NonNullable<string> = '';
  proposalNumber: NonNullable<string> = '';
  purchaseOrderNumber: NonNullable<number> = 0;
  resellerName: NonNullable<string> = '';
  shippingCommission: NonNullable<string> = 'R$ 0,00';
  shippingRate: NonNullable<string> = 'R$ 0,00';
  totalValueInvoices: NonNullable<string> = 'R$ 0,00';
  readonly isOrder = this.isOrderValidation;
  readonly isOrderList = this.isOrderListValidation;

  constructor(orderDto?: IOrderDTO) {
    if (orderDto) {
      Object.assign(this, {
        ...orderDto,
        companyDocument: CompanyDocument(orderDto.companyDocument),
        invoiceDetails: this.adaptOrderInvoiceDetailList(orderDto),
        netCommission: this.formatCurrency(orderDto.netCommission),
        orderDate: this.formatDate(orderDto.orderDate),
        orderStatus: this.adaptOrderStatus(orderDto.orderStatus),
        shippingCommission: this.formatCurrency(orderDto.shippingCommission),
        shippingRate: this.formatCurrency(orderDto.shippingRate),
        totalValueInvoices: this.formatCurrency(orderDto.totalValueInvoices),
      });
    }
  }

  private adaptOrderInvoiceDetail(invoiceDetailDto: IOrderInvoiceDetailDTO): IOrderInvoiceDetail {
    return {
      invoiceNumber: invoiceDetailDto.invoiceNumber,
      invoiceStatus: this.adaptInvoiceStatus(invoiceDetailDto.invoiceStatus),
    };
  }

  private adaptOrderInvoiceDetailList({ invoiceDetails }: IOrderDTO): IOrderInvoiceDetailList {
    if (isNotEmptyArray(invoiceDetails)) {
      return invoiceDetails.map((invoiceDetail: IOrderInvoiceDetailDTO): IOrderInvoiceDetail => (
        this.adaptOrderInvoiceDetail(invoiceDetail)
      ));
    }

    return [];
  };

  private adaptInvoiceStatus(invoiceStatusDto: IInvoiceStatusOptions): IInvoiceStatusKeys {
    return INVOICE_STATUS_ENUM[invoiceStatusDto] as IInvoiceStatusKeys;
  }

  private adaptOrderStatus(orderStatusDto: IOrderStatusOptions): IOrderStatusKeys {
    return ORDER_STATUS_ENUM[orderStatusDto] as IOrderStatusKeys;
  }

  private formatCurrency(value: number): string {
    return convertToBrCurrency(value);
  }

  private formatPercents(value: number): string {
    return convertToPercents(value);
  }

  private formatDate(date: string): string {
    return convertToBrDate(date);
  }

  private isOrderValidation(arg: unknown): arg is IOrder {
    return (
      !!arg
      && typeof arg === 'object'
      && validateProperties(arg)
      && typeof arg.orderNumber === 'string'
      && typeof arg.orderDate === 'string'
      && typeof arg.purchaseOrderNumber === 'number'
      && typeof arg.proposalNumber === 'string'
      && typeof arg.finalCustomerName === 'string'
      && typeof arg.resellerName === 'string'
      && typeof arg.paymentMethod === 'string'
      && typeof arg.orderStatus === 'string'
      && typeof arg.amountInvoices === 'number'
      && typeof arg.totalValueInvoices === 'string'
      && typeof arg.shippingRate === 'string'
      && typeof arg.shippingCommission === 'string'
      && typeof arg.netCommission === 'string'
      && typeof arg.itemsPercentageInvoiced === 'number'
      && typeof arg.allInvoicesTransmitted === 'boolean'
      && validateInvoiceDetails(arg.invoiceDetails)
      && validateCompanyDocument(arg.companyDocument)
      && typeof arg.isOrder === 'function'
      && typeof arg.isOrderList === 'function'
    );

    function validateProperties(arg: object): arg is Order {
      const fields = Object.keys(new Order());
      return fields.every(field => field in arg);
    }

    function validateInvoiceDetails(arg: unknown): arg is IOrderInvoiceDetailList {
      const invoiceDetailListLike = (
        !!arg
        && Array.isArray(arg)
        && arg.every((invoiceDetail: any) => validateInvoiceDetail(invoiceDetail))
      );

      return isNull(arg) || invoiceDetailListLike;
    }

    function validateInvoiceDetail(arg: unknown): arg is IOrderInvoiceDetail {
      return (
        !!arg
        && typeof arg === 'object'
        && 'invoiceNumber' in arg
        && 'invoiceStatus' in arg
        && typeof arg.invoiceNumber === 'string'
        && typeof arg.invoiceStatus === 'string'
      );
    }

    function validateCompanyDocument(arg: unknown): arg is ICompanyDocument {
      return (
        !!arg
        && typeof arg === 'object'
        && 'unmasked' in arg
        && typeof arg.unmasked === 'string'
        && 'masked' in arg && typeof arg.masked === 'string'
        && 'isValid' in arg && typeof arg.isValid === 'boolean'
        && 'isCompanyDocumentLike' in arg && typeof arg.isCompanyDocumentLike === 'boolean'
      );
    }
  };

  private isOrderListValidation(arr: unknown): arr is IOrderList {
    return Array.isArray(arr) && isNotEmptyArray(arr) && arr.every((item: any) => this.isOrder(item));
  };
}
